import {
  FeatureIds,
  isCapabilityAvailable,
  isLegacyPdtiCompany,
  RegistryType,
} from "@redbelly-tokeniser/mystake-common-sdk";

export { isLegacyPdtiCompany, PDTI_FULL_LEGACY_CUTOFF } from "@redbelly-tokeniser/mystake-common-sdk";

const PDTI_RESTRICTED_FEATURES = [
  FeatureIds.ADD_INVESTORS,
  FeatureIds.ADMIN_INVESTORS_LIST_PAGE,
  FeatureIds.SUBSCRIPTION_AGREEMENTS_FEATURE,
];

export type CompanyFeatures = { [key: string]: boolean };

/** Strict `=== true`, so a missing or not-yet-loaded flag reads as off. */
export const isFeatureOn = (features: CompanyFeatures | null | undefined, id: FeatureIds): boolean =>
  features?.[id] === true;

export const resolveCompanyFeatures = (
  features?: CompanyFeatures | null,
  companyCreatedAt?: Date | string | null
): CompanyFeatures => {
  const sourceFeatures = features ?? {};
  const isPdtiFullEnabled = sourceFeatures[FeatureIds.PDTI_FULL] === true || isLegacyPdtiCompany(companyCreatedAt);

  const resolvedFeatures = PDTI_RESTRICTED_FEATURES.reduce(
    (resolved, feature) => ({
      ...resolved,
      [feature]: isPdtiFullEnabled && sourceFeatures[feature] === true,
    }),
    { ...sourceFeatures, [FeatureIds.PDTI_FULL]: isPdtiFullEnabled }
  );

  return {
    ...resolvedFeatures,
    [FeatureIds.FUNDRAISE_FEATURES]: sourceFeatures[FeatureIds.FUNDRAISE_FEATURES] === true,
  };
};

export const canShowWalletFeature = (features: CompanyFeatures): boolean => isFeatureOn(features, FeatureIds.PDTI_FULL);

/** Subscription price prices a fundraise offer or a new-model application; a fund with neither never sees it. */
export const canShowSubscriptionPrice = (features: CompanyFeatures, isFundRegistry: boolean): boolean =>
  isFundRegistry &&
  (isFeatureOn(features, FeatureIds.FUNDRAISE_FEATURES) ||
    isFeatureOn(features, FeatureIds.USE_NEW_SUBSCRIPTION_MODEL));

/**
 * Subscriptions are fund-only and assigned per fund, so both halves are asked: the flag reaches this
 * company, and the capability applies to this registry type.
 */
export const isSubscriptionsOffered = (features: CompanyFeatures, registryType: RegistryType): boolean =>
  isFeatureOn(features, FeatureIds.USE_NEW_SUBSCRIPTION_MODEL) &&
  isCapabilityAvailable(FeatureIds.USE_NEW_SUBSCRIPTION_MODEL, registryType);

/** Yield accrual is fund-only and assigned per fund, so both halves are asked, as for subscriptions. */
export const isYieldOffered = (features: CompanyFeatures, registryType: RegistryType): boolean =>
  isFeatureOn(features, FeatureIds.USE_YIELD_ACCRUAL) &&
  isCapabilityAvailable(FeatureIds.USE_YIELD_ACCRUAL, registryType);

/** A yield fund: offered yield and the fund's accrual switch is on. Every class in it bears yield. */
export const isYieldFund = (features: CompanyFeatures, registryType: RegistryType, accrues: boolean): boolean =>
  isYieldOffered(features, registryType) && accrues;

/** Redemptions is fund-only; enablement is the fund's permit switch, not Feature assignment. */
export const isRedemptionsAvailable = (registryType: RegistryType): boolean =>
  isCapabilityAvailable(FeatureIds.REDEMPTIONS, registryType);

export const isRedemptionsOffered = (registryType: RegistryType, allowRedemptions?: boolean): boolean =>
  isRedemptionsAvailable(registryType) && allowRedemptions === true;

/** Redemption price is opt-in per fund: hidden unless the fund permits redemptions. */
export const canShowRedemptionPrice = (registryType: RegistryType, allowRedemptions?: boolean): boolean =>
  isRedemptionsOffered(registryType, allowRedemptions);
